import {
  Box,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogContent,
} from "@mui/material";
import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import DataToolbar from "../../components/common/DataToolbar";
import BackdropLoader from "../../components/common/BackdropLoader";
import type { AppDispatch } from "../../store";
import { LoadingButton } from "@mui/lab";
import {
  setDateRange,
  setPage,
  setRowsPerPage,
  setSearchTerm,
} from "../../store/slices/ApproverRuletableSlice";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import {
  useGetAllApproversQuery,
  useDeleteApproverMutation,
} from "../../api/services/approverApi";

import { useLazyGetAllPlantsDetailsQuery } from "../../api/services/plantApi";
import listAltCheckIcon from "../../assets/images/icons/list_alt_check.png";
import DataTable from "../../components/common/DataTable";

export interface RuleItem {
  id: number;
  approverName: string;
  approverId: string;
  ruleId: string;
  plant: string;
  workCenter: string;
  materialCategory: string;
  plantdisplayname: string;
  plantname: string;
}

const RuleScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { searchTerm, rowsPerPage, currentPage, dateRange } = useSelector(
    (state: any) => state.ApproverruleTable
  );

  const { data, isLoading, refetch } = useGetAllApproversQuery({
    page: currentPage,
    limit: rowsPerPage,
    q: searchTerm,
  });

  const approvers = data?.approvers || [];
  const pagination = data?.pagination || {};

  const [getPlants, { data: plantsData }] = useLazyGetAllPlantsDetailsQuery();
  const plants = plantsData?.plants || [];

  const [deleteApprover, { isLoading: removeApprover }] =
    useDeleteApproverMutation();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<RuleItem | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    getPlants({});
  }, [getPlants]);

  //  SORTING STATE
  const [sortField, setSortField] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Prepare display data
  const ruleDatas = useMemo(() => {
    if (!approvers || !plants) return [];

    return approvers.map((a: any): RuleItem => {
      const plantInfo = plants.find(
        (p: any) => p.name === a.rule?.plant?.PLANTNAME
      );

      return {
        id: a.ID,
        approverName: a.APPROVER_NAME,
        approverId: String(a.APPROVER_ID),
        ruleId: String(a.RULE_ID),
        plant:
          plantInfo?.plantDisplayName || a.rule?.plant?.PLANTDISPLAYNAME || "",
        workCenter: a.rule?.WORK_CENTER || "",
        materialCategory: a.rule?.MATERIAL_CATEGORY || "",
        plantdisplayname:
          plantInfo?.plantDisplayName || a.rule?.plant?.PLANTDISPLAYNAME || "",
        plantname: plantInfo?.name || a.rule?.plant?.PLANTNAME || "",
      };
    });
  }, [approvers, plants]);

  //  SORTING LOGIC
  const sortedRuleData = useMemo(() => {
    const sorted = [...ruleDatas];

    if (sortField && sortOrder) {
      sorted.sort((a: any, b: any) => {
        if (sortOrder === "asc") return a[sortField] > b[sortField] ? 1 : -1;
        return a[sortField] < b[sortField] ? 1 : -1;
      });
    }

    return sorted;
  }, [ruleDatas, sortField, sortOrder]);

  const handleOpenRemovePopup = (row: RuleItem) => {
    setSelectedUser(row);
    setOpenDialog(true);
  };

  const handleConfirmRemove = async () => {
    if (!selectedUser) return;

    try {
      await deleteApprover(selectedUser.id).unwrap();
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Approver removed successfully",
        confirmButtonColor: "#003EB0",
      });
      refetch();
    } catch {
      Swal.fire({
        icon: "error",
        title: "Failed!",
        text: "Unable to delete approver",
      });
    }
    setOpenDialog(false);
  };

  const renderActions = (row: RuleItem) => (
    <Button
      variant="outlined"
      color="error"
      onClick={() => handleOpenRemovePopup(row)}
      sx={{
        borderRadius: "20px",
        fontSize: 12,
        px: 2,
        textTransform: "none",
      }}
    >
      Remove User
    </Button>
  );

  const columns = [
    {
      key: "approverName",
      label: "Approver Name",
      sortable: true,
    },
    {
      key: "plant",
      label: "Plant",
      sortable: true,
      render: (row: RuleItem) => `${row.plantname}-${row.plantdisplayname}`,
    },
    {
      key: "workCenter",
      label: "Work Center",
      sortable: true,
    },
    {
      key: "materialCategory",
      label: "Material Category",
      sortable: true,
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <BackdropLoader openStates={isLoading} />

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Approver Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure and manage approvers for spare part requests. Assign
            approval levels and ensure the correct workflow is followed before
            processing.
          </Typography>
        </Box>

        <Button
          variant="contained"
          sx={{
            backgroundColor: "#002060",
            color: "#fff",
            borderRadius: 3,
            textTransform: "none",
            fontSize: 12,
            mt: 1,
          }}
          onClick={() => navigate("/approver/create")}
        >
          Assign Approver
        </Button>
      </Box>

      <DataToolbar
        searchTerm={searchTerm}
        setSearchTerm={(v) => dispatch(setSearchTerm(v))}
        dateRange={dateRange}
        setDateRange={(v) => dispatch(setDateRange(v))}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={(v) => dispatch(setRowsPerPage(v))}
        currentPage={currentPage}
        setCurrentPage={(v) => dispatch(setPage(v))}
        totalPages={pagination.totalPages}
      />

      <Paper variant="outlined">
        <DataTable
          columns={columns}
          data={sortedRuleData}
          getRowId={(row) => String(row.id)}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={handleSort}
          emptyMessage="No approvers found"
          actionRenderer={renderActions}
        />
      </Paper>

      {/* Delete Modal */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          sx: {
            p: 0.5,
            borderRadius: "16px",
            overflow: "hidden",
            width: 420,
            background: "linear-gradient(to bottom, #ffffffff, #ffffffff)",
            boxShadow:
              "0px 10px 30px rgba(0,0,0,0.18), 0px 4px 10px rgba(0,0,0,0.10)",
          },
        }}
      >
        {/* Header content */}
        <Box
          sx={{
            background: "linear-gradient(to bottom, #c0cce8ff, #FFFFFF)",
            textAlign: "center",
            padding: 1,
            py: 3,
            px: 4,
            borderTopRightRadius: 10,
            borderTopLeftRadius: 10,

            boxShadow:
              "inset 0px -10px 14px rgba(0,0,0,0.05), 0px 2px 6px rgba(0,0,0,0.06)",
          }}
        >
          <img
            src={listAltCheckIcon}
            alt="icon"
            style={{
              height: 38,
              marginBottom: 10,
              filter: "drop-shadow(0px 2px 3px rgba(255, 255, 255, 0.94))",
            }}
          />

          <Typography variant="h6" fontWeight={700}>
            Confirm your Removal approver
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: "#6B7280",
              px: 3,
              lineHeight: 1.4,
            }}
          >
            Are you sure you want to remove{" "}
            <strong>{selectedUser?.approverName}</strong>?
          </Typography>
        </Box>

        {/* Footer + Actions */}
        <DialogContent
          sx={{
            textAlign: "center",
            pb: 3,
            pt: 2,
            background: "linear-gradient(to bottom, #ffffffff, #ffffffff)",
            boxShadow: "inset 0px 6px 10px rgba(255, 255, 255, 1)",
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
          }}
        >
          <Box display="flex" justifyContent="center" gap={2} mt={1}>
            <Button
              variant="outlined"
              onClick={() => setOpenDialog(false)}
              sx={{
                borderRadius: "12px",
                px: 4,
                py: 1.2,
                borderColor: "#002060",
                color: "#002060",
                fontWeight: 500,
                textTransform: "none",
                width: 130,
                "&:hover": {
                  borderColor: "#001553",
                },
              }}
            >
              Cancel
            </Button>

            <LoadingButton
              variant="contained"
              loading={removeApprover}
              onClick={handleConfirmRemove}
              sx={{
                borderRadius: "12px",
                px: 4,
                py: 1.2,
                backgroundColor: "#002060",
                "&:hover": { backgroundColor: "#001553" },
                fontWeight: 500,
                textTransform: "none",
                width: 180,
                boxShadow: "0px 4px 14px rgba(0, 0, 0, 0.18)",
              }}
            >
              Proceed
            </LoadingButton>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RuleScreen;
